import React, { useMemo } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Slide } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import { useDealerPortalMenuProducts, useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import { useTheme } from '@emotion/react';
import { DealerPortalSectionEnumValue } from 'enums/DealerPortalSectionEnum';
import { DealerPortalMenuProductTypeEnumValue } from 'enums/DealerPortalMenuProductTypeEnum';
import UsefulLinkCard from './Components/UsefulLinkCard';

const useStyles = makeStyles(theme => ({
  pageFull: {
    width: '100%',
    '& ul': {
      marginLeft: '2rem',
    },
    '& a': {
      color: theme.palette.grey[800],
      fontStyle: 'italic',
    },
  },
}));

const getOrderedChildren = menuNode => {
  if (Array.isArray(menuNode?.children)) {
    return [...menuNode.children].sort((left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0));
  }

  if (Array.isArray(menuNode?.Children)) {
    return [...menuNode.Children].sort((left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0));
  }

  return [];
};

const getMenuId = menuNode => menuNode?.id || menuNode?.Id || null;

const getFirstOrderedMenuId = menuNodes => {
  const sortedNodes = [...(Array.isArray(menuNodes) ? menuNodes : [])].sort(
    (left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0),
  );

  if (sortedNodes.length === 0) {
    return null;
  }

  const firstNode = sortedNodes[0];
  const childFirstMenuId = getFirstOrderedMenuId(getOrderedChildren(firstNode));

  return childFirstMenuId || getMenuId(firstNode);
};

const normalizeCollection = collectionOrWrapped => {
  if (Array.isArray(collectionOrWrapped)) {
    return collectionOrWrapped;
  }

  if (Array.isArray(collectionOrWrapped?.items)) {
    return collectionOrWrapped.items;
  }

  return [];
};

const getProductType = product => product?.productType || product?.ProductType;

const UsefulLinks = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { dealerPortalMenuId } = useParams();
  const accountReducer = useSelector(state => state.AccountReducer);
  const isLoggedIn = Boolean(accountReducer?.isLoggedIn);

  const { t } = useTranslation();

  const {
    data: usefulLinksMenuTreeData,
    isFetching: gettingUsefulLinksMenuTree,
    isFetched: isUsefulLinksMenuTreeFetched,
  } = useDealerPortalMenuTree(DealerPortalSectionEnumValue.UsefulLinks, isLoggedIn);

  const resolvedDealerPortalMenuId = useMemo(() => {
    if (dealerPortalMenuId) {
      return dealerPortalMenuId;
    }

    return getFirstOrderedMenuId(usefulLinksMenuTreeData);
  }, [dealerPortalMenuId, usefulLinksMenuTreeData]);

  const { data: menuProductsData, isFetching: gettingUsefulLinks, isFetched } = useDealerPortalMenuProducts(
    resolvedDealerPortalMenuId,
    Boolean(isLoggedIn && resolvedDealerPortalMenuId),
  );

  const usefulLinks = useMemo(() => {
    return normalizeCollection(menuProductsData?.products || menuProductsData?.Products)
      .filter(product => getProductType(product) === DealerPortalMenuProductTypeEnumValue.UsefulLink)
      .map(product => {
        const usefulLink = product?.usefulLink || product?.UsefulLink;

        if (usefulLink) {
          return usefulLink;
        }

        return {
          id: product?.productId || product?.ProductId || product?.id || product?.Id,
          name: product?.name || product?.Name || '',
          description: product?.description || product?.Description || '',
          warning: product?.shortDescription || product?.ShortDescription || '',
          websiteUrl: '',
          pdfTitleUrl: '',
          pdfUrl: '',
        };
      })
      .filter(usefulLink => Boolean(usefulLink?.id));
  }, [menuProductsData]);

  const hasResolvedUsefulLinksMenu = Boolean(resolvedDealerPortalMenuId);
  const showNoDataMessage =
    (!hasResolvedUsefulLinksMenu && isUsefulLinksMenuTreeFetched && !gettingUsefulLinksMenuTree) ||
    (isFetched && usefulLinks.length === 0);

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull}>
        <GridContainer sx={{ mt: 1, mx: 2 }}>
          {!gettingUsefulLinks &&
            usefulLinks?.map(usefulLink => <UsefulLinkCard key={usefulLink.id} usefulLink={usefulLink} />)}

          {showNoDataMessage && (
            <GridItem xs={12}>
              <GridContainer alignContent={'center'} justifyContent={'center'}>
                <GridItem
                  xs={6}
                  md={4}
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    boxShadow: '0.1rem 0.8rem 2rem black',
                    padding: '1.5rem',
                    textAlign: 'center',
                  }}>
                  {t('no_useful_links_available')}
                </GridItem>
              </GridContainer>
            </GridItem>
          )}

          {gettingUsefulLinks &&
            [1, 2, 3, 4].map(l => (
              <GridItem key={l} xs={12} md={3} style={{ minHeight: '3rem' }}>
                <SkeletonWrapper
                  skeletonProps={{
                    height: 400,
                  }}
                  isLoading={gettingUsefulLinks}
                />
              </GridItem>
            ))}
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default UsefulLinks;
